import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { useUiLang, type UiLang } from "../../../../stores/uiLang";

// Run E2 J2: shared layout for the Project Explore help articles (K-5).
// Each article passes its text in English and Spanish; the page follows the
// shared UI language. Facts come from the app itself (teacher dashboard,
// Class Management, Assign PE Chapter) and from context/PE_RULES_FOR_CC.md.

export interface HelpArticleText {
  title: string;
  intro: string;
  steps: ReactNode[];
  ordered?: boolean;
  tip?: ReactNode;
}

const BACK: Record<UiLang, string> = {
  en: "Back to the Help Center",
  es: "Volver al Centro de ayuda",
};

export default function HelpArticle({
  text,
}: {
  text: Record<UiLang, HelpArticleText>;
}) {
  const lang = useUiLang();
  const t = text[lang];
  const List = t.ordered === false ? "ul" : "ol";
  return (
    <div className="bg-zinc-50 pt-20 min-h-screen">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-16 sm:py-20 text-white text-center px-4">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4">{t.title}</h1>
        <p className="text-lg max-w-2xl mx-auto">{t.intro}</p>
      </section>
      <section className="py-12 sm:py-16 px-6 max-w-3xl mx-auto text-gray-800">
        <List
          className={`${
            t.ordered === false ? "list-disc" : "list-decimal"
          } pl-6 space-y-3`}
        >
          {t.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </List>
        {t.tip && (
          <div className="mt-8 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded">
            <p className="text-indigo-800 font-medium">{t.tip}</p>
          </div>
        )}
        <p className="mt-10">
          <Link
            to="/support/help-center"
            className="text-indigo-700 hover:text-indigo-900 font-medium"
          >
            {BACK[lang]}
          </Link>
        </p>
      </section>
    </div>
  );
}
